import React, { useRef, useState, useEffect } from 'react';

import { animated, useSpring } from '@react-spring/web';
import browserInfo from 'browser-info';

import API from 'files/api.js';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import Checkbox from 'views/Checkbox.js';
import Request from 'files/Request.js';
import Spline from '@splinetool/react-spline';
import TextField from 'views/TextField.js';
import Utils from 'files/Utils';

export const runLogin = (utils, props) => {

    return new Promise(async (resolve, reject) => {
        try {

            // verify the username and password have been provided
            let { auto_login, password, refresh_token, single_use_token, username } = props || {};
            if(!refresh_token && !single_use_token && (!username || !password)) {
                throw new Error('Please enter your username and password to continue');
            }

            // prepare device name and operating system
            let { name, os } = browserInfo();
            
            // send request to server
            let { user } = await Request.post(utils, '/users/', {
                browser: name,
                password: password,
                platform: os,
                refresh_token: refresh_token,
                single_use_token: single_use_token,
                type: 'login',
                username: username
            });

            // write refresh token to local storage if auto login is enabled
            if(auto_login === true && user.refresh_token) {
                localStorage.setItem('refresh_token', user.refresh_token);
            }

            // remove refresh token if auto login is not enabled
            if(auto_login === false) {
                localStorage.removeItem('refresh_token');
            }

            // notify subscribers that login has completed
            resolve({ user });

        } catch(e) {
            reject(e);
        }
    });
}

const Login = ({ onLogin, utils }) => {

    const credentials = useRef({}); 

    const [autoLogin, setAutoLogin] = useState(false);
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState(null);
    const [username, setUsername] = useState(null);

    const [animations, animationsApi] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 22 },
        opacity: 0,
        transform: 'scale(0.5)'
    }));

    const onRunLogin = async ({ login_with_token = false } = {}) => {
        try {

            // set loading flag and start root loader
            setLoading(true);

            // submit login request to login processor
            let result = await runLogin(utils, {
                auto_login: autoLogin,
                ...credentials.current
            });

            // remove keypress listener
            document.removeEventListener('keydown', onKeyDown);

            // animate components out of view if auto login is not occurring
            if(login_with_token === false) {
                animationsApi.start({
                    opacity: 0,
                    transform: 'scale(0.5)'
                });
                await Utils.sleep(1);
            }

            // send account details back to root view
            setLoading(false);
            onLogin(result);

        } catch(e) {

            // remove refresh token if login failed due to invalid token
            if(credentials.current.auto_login === true) {
                localStorage.removeItem('refresh_token');
            }

            // end loading and show error alert
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue logging into your account. ${e.message || 'An unknown error occurred'}`,
                onClick: onShowLoginWindow
            });
        }
    }

    const onShowLoginWindow = () => {
        animationsApi.start({
            opacity: 1,
            transform: 'scale(1)'
        });
    }

    const onKeyDown = evt => {
        if(evt.keyCode === 13) {
            evt.preventDefault();
            onRunLogin();
        }
    }

    const onResetPasswordClick = () => {
        window.open(`${API.server}/reset-password`);
    }

    const getContent = () => {
        return (
            <animated.div style={{
                maxWidth: 475,
                width: '100%',
                ...animations
            }}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    height: 250,
                    width: '100%'
                }}>
                    <div style={{
                        height: 250,
                        position: 'relative',
                        overflow: 'hidden',
                        width: 250
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: -125,
                        }}>
                            <Spline 
                            scene={window.theme === 'dark' ? 'https://prod.spline.design/05Rlu1Ed0BeAjoHQ/scene.splinecode' : 'https://prod.spline.design/05Rlu1Ed0BeAjoHQ/scene.splinecode'} />
                        </div>
                    </div>
                </div>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: 16
                }}>
                    <TextField
                    autoCapitalize={'none'}
                    placeholder={'Username'}
                    value={username}
                    useDelay={false}
                    onChange={text => setUsername(text)}
                    fieldStyle={{
                        color: Appearance.colors.text(),
                        fontSize: 12,
                        fontWeight: '500',
                    }}/>
                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        marginTop: 8,
                        minWidth: 0
                    }}>
                        <TextField
                        isSecure={true}
                        placeholder={'Password'}
                        value={password}
                        useDelay={false}
                        onChange={text => setPassword(text)}
                        fieldStyle={{
                            color: Appearance.colors.text(),
                            fontSize: 12,
                            fontWeight: 500
                        }}
                        containerStyle={{
                            width: '100%'
                        }} />
                        <img
                        className={'text-button'}
                        onClick={onResetPasswordClick}
                        src={window.theme === 'dark' ? 'images/help-badge-dark.png' : 'images/help-badge-light.png'}
                        style={{
                            height: 35,
                            marginLeft: 12,
                            objectFit: 'contain',
                            width: 35
                        }}/>
                    </div>
                    <div style={{
                        alignItems: 'center',
                        borderTop: `1px solid ${Appearance.colors.divider()}`,
                        display: 'flex',
                        flexDirection: 'row',
                        marginBottom: 16,
                        marginTop: 16,
                        paddingTop: 16,
                        textAlign: 'left'
                    }}>
                        <Checkbox
                        checked={autoLogin}
                        color={Appearance.colors.primary()}
                        onChange={setAutoLogin} />
                        <span 
                        className={'cursor-pointer'}
                        onClick={() => setAutoLogin(val => !val)}
                        style={{
                            ...Appearance.textStyles.subTitle(),
                            color: Appearance.colors.text(),
                            marginLeft: 8
                        }}>{'Keep me signed in'}</span>
                    </div>
                    <Button
                    color={'primary'}
                    label={'Login'}
                    loading={loading}
                    onClick={onRunLogin}
                    transition={{
                        icon: 'key-icon-white.png'
                    }}
                    type={'transition'}/>
                </div>
            </animated.div>
        )
    }

    useEffect(() => {
        credentials.current = { password, username };
    }, [password, username]);

    const runAutoLogin = () => {
        
        // determine if a refresh token is stored and auto login should be completed
        let refresh_token = localStorage.getItem('refresh_token');
        if(refresh_token) {
            credentials.current = { ...credentials.current, auto_login: true, refresh_token };
            onRunLogin({ login_with_token: true });
            return;
        }

        // fallback to showing login window
        onShowLoginWindow();
    }

    useEffect(() => {

        // determine if auto login is enabled
        runAutoLogin();

        // add event listener for login window
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        }
    }, []);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
        }}>
            {getContent()}
        </div>
    )
}

export default Login;
